import { z } from 'zod/v4';

export const AdminGetFacilityProcessParamsSchema = z.object({
  id: z.string().min(1),
});

export type AdminGetFacilityProcessParams = z.infer<
  typeof AdminGetFacilityProcessParamsSchema
>;

/** Per-language overrides keyed by ISO 639-1 code, e.g. `{ de: { name: 'Spinnerei' } }`. */
export const FacilityProcessTranslationsSchema = z.record(
  z.string(),
  z.object({ name: z.string().optional() }),
);

/**
 * The `data` payload of `GET /api/admin/facility-processes/:id`. `translations`
 * always carries an `en` entry — the service falls back to `name` when none is
 * stored.
 */
export const AdminFacilityProcessSchema = z.object({
  id: z.string(),
  name: z.string(),
  productionProcessId: z.string().nullable(),
  translations: FacilityProcessTranslationsSchema,
});

export type AdminFacilityProcess = z.infer<typeof AdminFacilityProcessSchema>;

/** The body of `POST /api/admin/facility-processes` — the legacy route's limits. */
export const AdminCreateFacilityProcessBodySchema = z.object({
  name: z.string().min(1).max(60),
  productionProcessId: z.string().min(1).max(36),
});

export type AdminCreateFacilityProcessBody = z.infer<
  typeof AdminCreateFacilityProcessBodySchema
>;

/** A language's override, as written — `name` only, like the legacy schema. */
const FacilityProcessTranslationInputSchema = z.object({
  name: z.string().min(1),
});

/**
 * The languages records can be translated into — the legacy `LANGUAGES` list.
 * Other keys are stripped, as legacy's ajv did; at least one must remain.
 */
export const FacilityProcessTranslationsInputSchema = z
  .object({
    en: FacilityProcessTranslationInputSchema.optional(),
    es: FacilityProcessTranslationInputSchema.optional(),
    de: FacilityProcessTranslationInputSchema.optional(),
    fr: FacilityProcessTranslationInputSchema.optional(),
  })
  .refine(t => Object.values(t).some(v => v !== undefined), {
    message: 'translations must contain at least one language',
  });

/**
 * The body of `PUT /api/admin/facility-processes/:id`. `name` and
 * `productionProcessId` are required on every update; omitting `translations`
 * leaves the stored ones untouched.
 */
export const AdminUpdateFacilityProcessBodySchema =
  AdminCreateFacilityProcessBodySchema.extend({
    translations: FacilityProcessTranslationsInputSchema.optional(),
  });

export type AdminUpdateFacilityProcessBody = z.infer<
  typeof AdminUpdateFacilityProcessBodySchema
>;
